import { useCallback, useEffect, useState } from 'react';
import { usePrevious } from './usePrevious';
import { AnyObject, LibbyObject } from '../types/types';

/*
  This hook is used in combination with LibbyFetchDAO to do calls with libby with basic
  fetch more pagination
  params:
  - libby: Libby object provided by DatabaseConnector
  - Options object:
    - filter: Filter object to be passed to the dao (see LibbyFetchDAO)
              WARNING the hook react in filter changes. Use memo or ref hooks
              to memoize filter value and avoid unwanted api calls
    - orderBy: property for the order by (accept libby property, example: name.first)
    - daoName: name of the dao to use (must be connected with the libby HOC)
    - limit: limit to use for the calls (optional, default 40)
  returns an object with:
  - data: all the data fetched (incrementally by fetchMore)
  - working: boolean, can be used like a loading state
  - fetchMore: function to call to fetch the next batch of data
  - reFetch : function to reset the hook (offset = 0, data = 0)
              WARNING this function is automatically called when filter option change
  Example of use
  const { data: establishments, working, fetchMore } = useLibbyFetch(libby, {
    orderBy: 'nombre',
    daoName: 'establishment',
    filter, // see LibbyFetchDAO for an example
    limit: 20,
  })
 */

export type OptionsLibbyFetch = {
  filter?: AnyObject;
  orderBy?: string;
  daoName: string;
  limit?: number;
  checkDuplication?: boolean;
  direction?: string;
};

export const useLibbyFetch = (
  libby: LibbyObject,
  {
    filter,
    orderBy,
    direction = 'asc',
    daoName = '',
    limit: initialLimit = 40,
    checkDuplication = true,
  }: OptionsLibbyFetch,
) => {
  const prevFilter = usePrevious(filter);
  const [endReached, setEndReached] = useState(false);
  const [initialFetch, setInitialFetch] = useState(false);
  const [mergedData, setMergedData] = useState<AnyObject[]>([]);
  const [limit] = useState(initialLimit);
  const [offset, setOffset] = useState(0);
  const fetch = useCallback(async () => {
    if (!daoName) {
      // eslint-disable-next-line
      console.log('daoName required!');
    } else if (!libby[daoName]) {
      // eslint-disable-next-line
      console.log(
        'DAO not found, be sure that DatabaseConnector HOC has the daoName in its config',
      );
    } else if (!endReached) {
      const data = await libby[daoName].fetch({
        filter,
        orderBy,
        limit,
        offset,
        direction,
      });

      // TODO: think how to filter duplicated in a better way
      setMergedData((prev: AnyObject[]) => {
        // this is to avoid duplications
        const copy: AnyObject[] = [...prev];
        const { pk } = libby[daoName];
        if (data && data.length) {
          data.forEach((item: AnyObject) => {
            if (!checkDuplication || !copy.find((el) => el[pk] === item[pk])) {
              copy.push(item);
            }
          });
        }
        return copy;
      });
      setOffset(offset + limit);
      if (!data?.length) {
        setEndReached(true);
      }
    }
  }, [checkDuplication, daoName, filter, libby, limit, offset, orderBy, direction, endReached]);
  const reFetch = useCallback(() => {
    setMergedData([]);
    setOffset(0);
    setEndReached(false);
    setInitialFetch(false);
  }, []);
  useEffect(() => {
    reFetch();
  }, [orderBy, direction, reFetch]);
  useEffect(() => {
    if (prevFilter !== filter) {
      reFetch();
    }
  }, [filter, prevFilter, reFetch]);
  useEffect(() => {
    if (!initialFetch) {
      setInitialFetch(true);
      fetch();
    }
  }, [fetch, initialFetch]);

  const [debounce, setDebounce] = useState<NodeJS.Timeout | null>(null);
  const fetchMore = useCallback(() => {
    if (debounce) {
      clearTimeout(debounce);
    }
    setDebounce(
      setTimeout(() => {
        fetch();
      }, 100),
    );
  }, [debounce, fetch]);

  const addCreate = useCallback((data: any) => {
    setMergedData((prev) => {
      const copy: AnyObject[] = [...prev];
      copy.push(data);
      return copy;
    });
  }, []);

  const updateData = useCallback(
    (dataUpdate: AnyObject, id: string) => {
      setMergedData((prev) => {
        const copy: AnyObject[] = [...prev];
        if (dataUpdate[id]) {
          const result = copy.findIndex((value) => value[id] === dataUpdate[id]);
          copy[result] = dataUpdate;
        }
        return copy;
      });
    },
    [setMergedData],
  );

  return {
    data: mergedData,
    working: libby.working,
    fetchMore,
    reFetch,
    addCreate,
    setMergedData,
    updateData,
  };
};
